import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { withRouter, Redirect } from 'react-router-dom';

import CheckoutOrderingSteps from '../components/content/checkout/CheckoutOrderingSteps';
import CheckoutCompletedView from '../components/content/checkout/CheckoutCompletedView';
import FeedbackDialog from '../components/content/dialogs/FeedbackDialog';
import WorldpayNotificationDialog from '../components/content/dialogs/WorldpayNotificationDialog';
import { NotificationDialogConsumer } from '../components/context/NotificationDialogContext';

import ClearCartMutation from '../mutations/ClearCartMutation';

import { pathHasLoginToken } from '../utils/common';
import { trackEvent } from '../utils/ga-tracking';

class CheckoutPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      email: null,
      completed: false,
      sgOrderId: null,
      worldpayCallbackPath: null,
      showWorldpayNotificationDialog: false,
      showFeedbackDialog: false,
    };

    this.handleCloseFeedbackDialog = this.handleCloseFeedbackDialog.bind(this);
    this.handleOpenFeedbackDialog = this.handleOpenFeedbackDialog.bind(this);
    this.handleShowCart = this.handleShowCart.bind(this);
  }

  componentDidMount() {
    const { match, history, location, viewer } = this.props;
    if (pathHasLoginToken(location.search)) {
      history.replace(location.pathname);
    }

    if (match.params.callbackUrl) {
      if (viewer.cart && viewer.cart.order && viewer.cart.order.orderid) {
        switch (match.params.callbackUrl) {
          case 'worldpay-pending':
            this.handleOpenWorldpayNotificationDialog('Pending');
            return;
          case 'worldpay-cancel':
            this.handleOpenWorldpayNotificationDialog('Cancel');
            trackEvent('Checkout', 'Canceled payment', 'Worldpay payment canceled');
            return;
          case 'worldpay-failure':
            this.handleOpenWorldpayNotificationDialog('Failure');
            trackEvent('Checkout', 'Failed payment', 'Worldpay failed', {
              nonInteraction: true,
            });
            return;
          case 'worldpay-error':
            this.handleOpenWorldpayNotificationDialog('Error');
            trackEvent('Checkout', 'Failed payment', 'Worldpay error', {
              nonInteraction: true,
            });
            return;
          default:
            this.handleCheckoutCompleted(
              viewer.cart.order.orderid,
              viewer.cart.customer.email
            );
            return;
        }
      }
    }
  }

  handleCheckoutCompleted = (orderId, email) => {
    this.handleClearCart();
    this.setState({
      completed: true,
      sgOrderId: orderId,
      email: email,
    });

    trackEvent('Checkout', 'Completed checkout', 'Checkout successfull', {
      nonInteraction: true,
    });
  };

  handleOpenWorldpayNotificationDialog = callbackPath => {
    this.setState({
      showWorldpayNotificationDialog: true,
      worldpayCallbackPath: callbackPath,
    });
  };

  handleCloseWorldpayNotificationDialog = () => {
    if (this.state.worldpayCallbackPath === 'Pending') {
      this.handleClearCart();
    } else {
      this.props.history.replace('/checkout');
    }
    this.setState({
      showWorldpayNotificationDialog: false,
      worldpayCallbackPath: null,
    });
  };

  handleCloseFeedbackDialog() {
    this.setState({ showFeedbackDialog: false });
  }

  handleClearCart = () => {
    const { viewer, relay } = this.props;
    ClearCartMutation.commit(relay.environment, viewer.cart.id);
  };

  handleOpenFeedbackDialog() {
    this.setState({ showFeedbackDialog: true });
  }

  handleShowCart() {
    this.props.history.push('/cart');
  }

  render() {
    const { viewer, match, isLoggedIn } = this.props;

    const {
      completed,
      email,
      sgOrderId,
      worldpayCallbackPath,
      showWorldpayNotificationDialog,
      showFeedbackDialog,
    } = this.state;

    if (!completed) {
      if (!viewer.cart) {
        return <Redirect to="/" />;
      }
      if (match.params.callbackUrl) {
        if (!viewer.cart.order || (viewer.cart.order && !viewer.cart.order.orderid)) {
          return <Redirect to="/checkout" />;
        }
      }
    }

    return (
      <NotificationDialogConsumer>
        {({ onOpenNotificationDialog }) => (
          <div>
            <div className="checkout-page-background" />
            <div className="container-960 container-page checkout-page">
              <div className="checkout-container">
                {!completed && (
                  <CheckoutOrderingSteps
                    cart={viewer.cart}
                    viewer={viewer}
                    isLoggedIn={isLoggedIn}
                    onCheckoutCompleted={this.handleCheckoutCompleted}
                    onShowCart={this.handleShowCart}
                    onOpenNotificationDialog={onOpenNotificationDialog}
                  />
                )}
                {completed && (
                  <CheckoutCompletedView
                    email={email}
                    orderId={sgOrderId}
                    onOpenFeedbackClick={this.handleOpenFeedbackDialog}
                  />
                )}
              </div>
            </div>
            <WorldpayNotificationDialog
              open={showWorldpayNotificationDialog}
              onRequestClose={this.handleCloseWorldpayNotificationDialog}
              path={worldpayCallbackPath}
            />
            <FeedbackDialog
              open={showFeedbackDialog}
              showRating={true}
              onRequestClose={this.handleCloseFeedbackDialog}
            />
          </div>
        )}
      </NotificationDialogConsumer>
    );
  }
}

CheckoutPage.propTypes = {
  isLoggedIn: PropTypes.bool,
  viewer: PropTypes.object,
  relay: PropTypes.object,
  history: PropTypes.object,
  match: PropTypes.object,
  location: PropTypes.object,
};

export { CheckoutPage };

export default withRouter(
  createFragmentContainer(CheckoutPage, {
    viewer: graphql`
      fragment CheckoutPage_viewer on Viewer {
        cart {
          customer {
            email
          }
          id
          order {
            id
            orderid
          }
          ...CheckoutOrderingSteps_cart
        }
        ...CheckoutOrderingSteps_viewer
      }
    `,
  })
);
